import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import api, { money } from "../lib/api";
import { CheckCircle2, Loader2, XCircle, Zap } from "lucide-react";
import { Button } from "../components/ui/button";
import Logo from "../components/Logo";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | failed
  const [summary, setSummary] = useState(null);
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let active = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (!active) return;
        if (data.payment_status === "paid") {
          setSummary(data.summary);
          setState("paid");
          return;
        }
      } catch {}
      tries.current += 1;
      if (tries.current < 10) setTimeout(poll, 1500);
      else if (active) setState("failed");
    };
    poll();
    return () => { active = false; };
  }, [sessionId]);

  const sym = summary?.currency_symbol || "$";

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 p-8 text-center" data-testid="payment-success-page">
        <Link to="/dashboard" className="flex items-center justify-center gap-2 mb-6">
          <Logo size="sm" />
        </Link>

        {state === "checking" && (
          <div className="py-6">
            <Loader2 className="w-10 h-10 animate-spin text-emerald-500 mx-auto" />
            <p className="font-semibold text-slate-900 mt-4">Confirming your payment...</p>
          </div>
        )}

        {state === "paid" && (
          <div>
            <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto"><CheckCircle2 className="w-9 h-9 text-emerald-500" /></div>
            <h1 className="font-display text-2xl font-extrabold text-slate-900 mt-4">Payment successful</h1>
            {summary && (
              <>
                <p className="font-display text-4xl font-extrabold text-slate-900 mt-4">{money(summary.amount_paid, sym)} paid</p>
                <div className="flex justify-center gap-6 mt-4">
                  <div><p className="text-xs uppercase font-bold text-slate-400">You saved</p><p className="font-display font-bold text-emerald-600 text-lg">{money(summary.saved, sym)}</p></div>
                  <div><p className="text-xs uppercase font-bold text-slate-400">You earned</p><p className="font-display font-bold text-amber-600 text-lg">{summary.earned_points} pts</p></div>
                  {summary.cashback > 0 && <div><p className="text-xs uppercase font-bold text-slate-400">Cashback</p><p className="font-display font-bold text-emerald-600 text-lg">{money(summary.cashback, sym)}</p></div>}
                </div>
              </>
            )}
            <Button onClick={() => navigate("/dashboard")} data-testid="success-dashboard-btn" className="w-full mt-6 rounded-full bg-emerald-500 hover:bg-emerald-600 font-bold">Back to dashboard</Button>
          </div>
        )}

        {state === "failed" && (
          <div>
            <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center mx-auto"><XCircle className="w-9 h-9 text-amber-500" /></div>
            <h1 className="font-display text-2xl font-extrabold text-slate-900 mt-4">Couldn't confirm payment</h1>
            <p className="text-slate-500 mt-2">If you completed payment, it may take a moment. Check your bills shortly.</p>
            <Button onClick={() => navigate("/bills")} className="w-full mt-6 rounded-full bg-slate-900 hover:bg-slate-800 font-bold">Go to my bills</Button>
          </div>
        )}
      </div>
    </div>
  );
}
